import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { KeyRound, ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";

/** Pagina "Reimposta password" — /reset-password/:token */
export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const API = process.env.REACT_APP_BACKEND_URL;

  const passwordScore = (p) => {
    let s = 0;
    if (p.length >= 8)  s += 1;
    if (p.length >= 12) s += 1;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s += 1;
    if (/[0-9]/.test(p)) s += 1;
    if (/[^A-Za-z0-9]/.test(p)) s += 1;
    return s; // 0-5
  };
  const score = passwordScore(pwd);
  const scoreLabel = ["Troppo debole", "Debole", "Media", "Buona", "Forte", "Eccellente"][score];
  const scoreColor = ["bg-red-500", "bg-red-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-500"][score];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) return setError("La password deve essere lunga almeno 8 caratteri.");
    if (pwd !== confirm) return setError("Le due password non coincidono.");
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Errore durante l'aggiornamento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020615] text-white flex items-center justify-center px-4 py-12"
         data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-md p-8 shadow-2xl">
          <div className="mb-6 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#10B981] mb-4">
              <KeyRound className="h-7 w-7 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Imposta nuova password</h1>
            <p className="text-sm text-slate-300/80 mt-2">
              Scegli una password robusta: almeno 8 caratteri, con maiuscole, minuscole e numeri.
            </p>
          </div>

          {!done ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pwd" className="text-slate-200 text-xs font-semibold">Nuova password</Label>
                <div className="relative">
                  <Input id="pwd" type={show ? "text" : "password"} value={pwd}
                         onChange={(e) => setPwd(e.target.value)}
                         placeholder="Almeno 8 caratteri" required autoFocus
                         data-testid="reset-pwd"
                         className="mt-1 h-11 pr-11 bg-white/[0.08] border-white/15 text-white placeholder:text-slate-400
                                    focus:border-emerald-400 focus:ring-emerald-400/30" />
                  <button type="button" onClick={() => setShow((v) => !v)}
                          data-testid="reset-toggle-visibility"
                          className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 hover:text-white">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {pwd && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full ${scoreColor} transition-all`}
                           style={{ width: `${(score / 5) * 100}%` }} />
                    </div>
                    <span className="text-[11px] text-slate-300 min-w-[80px] text-right"
                          data-testid="reset-score-label">{scoreLabel}</span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirm" className="text-slate-200 text-xs font-semibold">Ripeti password</Label>
                <Input id="confirm" type={show ? "text" : "password"} value={confirm}
                       onChange={(e) => setConfirm(e.target.value)}
                       placeholder="Ripeti la nuova password" required
                       data-testid="reset-confirm"
                       className="mt-1 h-11 bg-white/[0.08] border-white/15 text-white placeholder:text-slate-400
                                  focus:border-emerald-400 focus:ring-emerald-400/30" />
                {confirm && pwd !== confirm && (
                  <p className="mt-1 text-xs text-red-300">Le due password non coincidono.</p>
                )}
              </div>

              {error && (
                <div className="text-sm text-red-100 bg-red-500/20 border border-red-400/30 rounded-lg p-3"
                     data-testid="reset-error">{error}</div>
              )}

              <Button type="submit" disabled={loading || !pwd || !confirm || pwd !== confirm || score < 2}
                      data-testid="reset-submit"
                      className="w-full h-12 rounded-xl text-base font-semibold
                                 bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                                 hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                                 text-white shadow-lg shadow-emerald-500/25 disabled:opacity-60">
                {loading ? (<><SynapseLoader size="sm" inline className="mr-2" /> Aggiornamento…</>)
                        : "Aggiorna password"}
              </Button>
            </form>
          ) : (
            <div className="text-center py-6" data-testid="reset-success">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-500/20 mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="font-heading text-lg font-bold text-white mb-2">Password aggiornata</h2>
              <p className="text-sm text-slate-300/80">
                Ti stiamo reindirizzando al login…
              </p>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-emerald-300"
                  data-testid="link-back-login">
              <ArrowLeft className="h-3.5 w-3.5" /> Torna al login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
